import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components/ui/overlays/Modal/Modal";
import { Typography } from "@/components/ui/text/Typography/Typography";
import UploadsPageFileUploadRow from "@/components/uploads/modal/UploadsPageFileUploadRow";
import UploadsProfileForm, { type IUploadsProfileFormData } from "@/components/uploads/profile/UploadsProfileForm";
import useUploadedFilesBulkCreateProfiles from "@/components/uploads/useUploadedFilesBulkCreateProfiles";
import { ProfileUploadsContext, type UploadBatch } from "@/data/uploads/uploads.context";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  selectedFiles: File[];
  jobTitle?: string;
  metroArea?: string;
}

const UploadsPageFilesUploadModal = ({ isOpen, onClose, selectedFiles, jobTitle, metroArea }: IProps) => {
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);
  const [addToUploadQueue, setAddToUploadQueue] = useState<boolean>(false);
  const [profileData, setProfileData] = useState<IUploadsProfileFormData | null>(null);

  const { t } = useTranslation();

  const {
    currentUploadBatch,
    addBatchToQueue,
    setShowMinimizedWindow,
    removeBatchFromQueue,
    updateBatchMetadata,
    setCurrentUploadBatch,
    updateQueue,
  } = ProfileUploadsContext();

  const { processCompletedBatch } = useUploadedFilesBulkCreateProfiles();

  const handleSaveAndClose = (data: IUploadsProfileFormData) => {
    if (addToUploadQueue) {
      addBatchToQueue({
        files: selectedFiles.map((file) => ({ file, id: "", status: "idle" as const })),
        jobTitle: data?.jobPosition || "",
        location: data?.location || "",
        tags: data?.tags || [],
        uploadedMediaIds: [],
        isProcessing: false,
        hasBeenProcessed: false,
      });

      setShowMinimizedWindow(true);
      setAddToUploadQueue(false);
      onClose();

      return;
    }
    if (currentUploadBatch?.files.length !== currentUploadBatch?.uploadedMediaIds.length) {
      updateBatchMetadata(
        currentUploadBatch?.id || "",
        data?.tags || [],
        data?.jobPosition || "",
        data?.location || "",
      );
      if (currentUploadBatch?.id === activeBatchId) {
        setCurrentUploadBatch((curr) => {
          if (curr) {
            return {
              ...curr,
              jobTitle: data?.jobPosition || "",
              tags: data?.tags || [],
              location: data?.location || "",
            } satisfies UploadBatch;
          }
          return curr;
        });
        updateQueue(activeBatchId, {
          ...currentUploadBatch,
          tags: data?.tags || [],
          jobTitle: data?.jobPosition || "",
          location: data?.location || "",
        } satisfies UploadBatch);
      }
      setShowMinimizedWindow(true);
      onClose();
      return;
    }
    if (currentUploadBatch) {
      processCompletedBatch({
        ...currentUploadBatch,
        jobTitle: data.jobPosition,
        location: data.location,
        tags: data.tags,
      });
    }

    onClose();
  };

  useEffect(() => {
    if (!activeBatchId || !isOpen) {
      return;
    }
    updateBatchMetadata(
      activeBatchId,
      profileData?.tags || [],
      profileData?.jobPosition || "",
      profileData?.location || "",
    );
    if (currentUploadBatch?.id === activeBatchId) {
      setCurrentUploadBatch((curr) => {
        if (curr) {
          return {
            ...curr,
            jobTitle: profileData?.jobPosition || "",
            tags: profileData?.tags || [],
            location: profileData?.location || "",
          } satisfies UploadBatch;
        }
        return curr;
      });
      updateQueue(activeBatchId, {
        ...currentUploadBatch,
        tags: profileData?.tags || [],
        jobTitle: profileData?.jobPosition || "",
        location: profileData?.location || "",
      } satisfies UploadBatch);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (selectedFiles.length > 0 && !currentUploadBatch) {
      const batchId = addBatchToQueue({
        files: selectedFiles.map((file) => ({ file, id: "", status: "idle" as const })),
        tags: profileData?.tags || [],
        uploadedMediaIds: [],
        isProcessing: false,
        hasBeenProcessed: false,
        jobTitle: profileData?.jobPosition || "",
        location: profileData?.location || "",
      });
      setActiveBatchId(batchId);
      return;
    }

    setAddToUploadQueue(true);
    setActiveBatchId(null);

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedFiles]);

  const displayFiles =
    currentUploadBatch?.id === activeBatchId
      ? currentUploadBatch?.files || []
      : selectedFiles?.map((file) => ({ file, id: "", status: "idle" as const })) || [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (activeBatchId) {
          removeBatchFromQueue(activeBatchId);
        }
        onClose();
      }}
      title={t("uploads.files-upload-modal.title")}
      modalClassName="!max-h-[80vh] !max-w-[555px] !flex !flex-col"
    >
      <UploadsProfileForm
        onConfirm={handleSaveAndClose}
        onChange={setProfileData}
        subtitle={t("uploads.files-upload-modal.add-info-text")}
        {...(jobTitle && metroArea && { jobTitle, metroArea })}
      >
        <div className="flex w-full flex-1 flex-col gap-5">
          {displayFiles?.map((file, index) => (
            <UploadsPageFileUploadRow
              key={index}
              file={file.file}
              status={file.status}
            />
          ))}
        </div>
        <Typography
          size="body-3"
          variant="default-italic"
          className="text-center text-text-inverted-secondary"
        >
          {t("uploads.files-upload-modal.description")}
        </Typography>
      </UploadsProfileForm>
    </Modal>
  );
};

export default UploadsPageFilesUploadModal;
